import { useEffect, useState } from "react";
import { useGetIdentity } from "ra-core";
import type { ModuleId } from "@/modules/registry";
import { ModuleSetupDialog } from "@/modules/apps/ModuleSetupDialog";
import { useModuleSetup } from "@/modules/apps/useModuleSetup";
import { getActiveViewAsCompany } from "@/modules/platform/viewAsCompanyState";

const dismissedKey = (memberId: unknown, moduleId: ModuleId) =>
  `nomi.moduleSetup.dismissed.${String(memberId)}.${moduleId}`;

const readDismissed = (key: string) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

/**
 * First visit of an admin to a module that still needs setup (e.g. just
 * turned on from Apps or by the platform): open the setup guide once.
 * Closing it remembers the choice; the guide stays available in Apps.
 */
export const ModuleSetupPrompt = ({ moduleId }: { moduleId: ModuleId }) => {
  const { identity } = useGetIdentity();
  const isAdmin =
    (identity as { administrator?: boolean } | undefined)?.administrator ===
    true;
  const key = dismissedKey(identity?.id, moduleId);
  const eligible =
    isAdmin &&
    identity?.id != null &&
    !readDismissed(key) &&
    !getActiveViewAsCompany();
  const setup = useModuleSetup(moduleId, { enabled: eligible });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (eligible && !setup.isPending && !setup.isReady) setOpen(true);
  }, [eligible, setup.isPending, setup.isReady]);

  if (!eligible) return null;
  return (
    <ModuleSetupDialog
      moduleId={moduleId}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          try {
            localStorage.setItem(key, "1");
          } catch {
            // Storage blocked: the guide may show again next visit.
          }
        }
      }}
    />
  );
};
